/**
 * Client-side DRAFT state for the cafeteria Complete-service surface.
 *
 * The worker's reimbursable / non-reimbursable / leftover edits and the
 * per-deliverable service temperatures (the `ProductionEdits` map) are local
 * until the day is signed. This store persists that uncommitted draft to
 * localStorage so "Save for later" survives navigating away and back — keyed
 * by (school location × service date). A draft is dropped once it holds no
 * records (e.g. after a successful sign clears it), and drafts older than
 * `PRODUCTION_DRAFT_MAX_AGE_DAYS` are pruned so abandoned ones don't pile up.
 *
 * On reopen the page re-seeds the base from fresh server truth, then overlays
 * the persisted draft (`overlayProductionEdits`) so server-side read-only
 * fields like `served` stay current while the worker's in-progress entries win.
 *
 * Manual localStorage sync (no zustand persist middleware), mirroring the
 * serve-draft store.
 */

import { create } from 'zustand'

import { isTransportRowKey, isTransportTempKey, type ProductionEdits } from '@/lib/cafeteria/format'

const DRAFTS_KEY = 'ordo.cafeteria.productionDrafts'
const PRODUCTION_DRAFT_MAX_AGE_DAYS = 3
const DAY_MS = 24 * 60 * 60 * 1000

/** One complete-service session's uncommitted edits, plus a stamp for pruning. */
export interface ProductionDraft {
  edits: ProductionEdits
  updatedAt: number
}

/** Stable key for a production draft: a school location's service for one date. */
export function productionDraftKey(locationId: string, serviceDate: string): string {
  return `${locationId}::${serviceDate}`
}

function read<T>(key: string, fallback: T): T {
  if (typeof window === 'undefined') {
    return fallback
  }
  try {
    const raw = window.localStorage.getItem(key)
    return raw ? (JSON.parse(raw) as T) : fallback
  } catch {
    return fallback
  }
}

function write(key: string, value: unknown) {
  if (typeof window === 'undefined') {
    return
  }
  try {
    window.localStorage.setItem(key, JSON.stringify(value))
  } catch {
    // localStorage unavailable / quota — non-fatal.
  }
}

/** True when a draft holds no production records. Pure + tested. */
export function isProductionDraftEmpty(edits: ProductionEdits): boolean {
  return Object.keys(edits).length === 0
}

/** Drop drafts last touched before `now - maxAgeDays`. Pure + tested. */
export function pruneProductionDrafts(
  drafts: Record<string, ProductionDraft>,
  now: number,
  maxAgeDays: number = PRODUCTION_DRAFT_MAX_AGE_DAYS,
): Record<string, ProductionDraft> {
  const cutoff = now - maxAgeDays * DAY_MS
  let changed = false
  const next: Record<string, ProductionDraft> = {}
  for (const [key, draft] of Object.entries(drafts)) {
    if (draft.updatedAt >= cutoff) {
      next[key] = draft
    } else {
      changed = true
    }
  }
  return changed ? next : drafts
}

/**
 * Overlay a persisted draft onto a freshly-seeded base, keeping the base's
 * structure (production records / rows / temps that still exist server-side)
 * and the draft's worker-entered values where keys line up. Read-only fields
 * (e.g. `served`) live outside `ProductionEdits`, so the base's fresh server
 * truth is preserved while the worker's in-progress entries win. Pure + tested.
 */
export function overlayProductionEdits(base: ProductionEdits, draft: ProductionEdits): ProductionEdits {
  const merged: ProductionEdits = {}
  for (const [prId, basePr] of Object.entries(base)) {
    const draftPr = draft[prId]
    if (!draftPr) {
      merged[prId] = basePr
      continue
    }
    // Rows and temps overlay by key the BASE already has, so a row or a
    // deliverable that no longer exists server-side is never resurrected. The
    // unified input's own namespaces are the exception (PROD-1279): its
    // `facility::` / `delivery::` temperatures and its `transport::` rows have
    // no seed on the fresh base — the fill wire does not carry them — so they
    // are carried whenever the draft has them.
    const rows = { ...basePr.rows }
    for (const [rowKey, row] of Object.entries(draftPr.rows ?? {})) {
      if (rowKey in rows || isTransportRowKey(rowKey)) {
        rows[rowKey] = row
      }
    }
    const temps = { ...basePr.temps }
    for (const [tempKey, value] of Object.entries(draftPr.temps ?? {})) {
      if (tempKey in temps || isTransportTempKey(tempKey)) {
        temps[tempKey] = value
      }
    }
    // Write-ins are a worker-owned list, not a keyed overlay: a draft that
    // carries the list (even empty — the worker may have removed rows) wins
    // wholesale. Drafts persisted before write-ins existed have no key and
    // keep the fresh server seed. Rows persisted before the explicit
    // non-reimbursable field deserialize without it — derive the remainder
    // so an old draft never renders NaN in the new input.
    const writeIns = (draftPr.writeIns ?? basePr.writeIns).map((w) => ({
      ...w,
      nonReimbursableQty: w.nonReimbursableQty ?? Math.max(0, w.quantity - w.reimbursableQty),
    }))
    // The "Time reading" is a single scalar, so a draft that carries the key
    // wins outright (including a deliberately cleared `''`); a draft persisted
    // before the box existed keeps the fresh seed.
    const readingTime = 'readingTime' in draftPr ? draftPr.readingTime : basePr.readingTime
    // Same rule for the unified input's Delivery card scalars (PROD-1279).
    const deliveryReadingTime =
      'deliveryReadingTime' in draftPr ? draftPr.deliveryReadingTime : basePr.deliveryReadingTime
    const receiverName = 'receiverName' in draftPr ? draftPr.receiverName : basePr.receiverName
    merged[prId] = {
      rows,
      temps,
      writeIns,
      readingTime,
      ...(deliveryReadingTime === undefined ? {} : { deliveryReadingTime }),
      ...(receiverName === undefined ? {} : { receiverName }),
    }
  }
  return merged
}

interface ProductionDraftStore {
  drafts: Record<string, ProductionDraft>
  /** Read a session's draft edits (empty object when none persisted). */
  getDraft: (key: string) => ProductionEdits
  /** Persist a session's draft. Removes the entry when it holds nothing. */
  saveDraft: (key: string, edits: ProductionEdits) => void
  /** Drop a session's draft (after the day is signed). */
  clearDraft: (key: string) => void
}

export const useCafeteriaProductionDraftStore = create<ProductionDraftStore>((set, get) => ({
  drafts: read<Record<string, ProductionDraft>>(DRAFTS_KEY, {}),
  getDraft: (key) => get().drafts[key]?.edits ?? {},
  saveDraft: (key, edits) => {
    if (isProductionDraftEmpty(edits)) {
      get().clearDraft(key)
      return
    }
    const now = Date.now()
    const pruned = pruneProductionDrafts(get().drafts, now)
    const drafts = { ...pruned, [key]: { edits, updatedAt: now } }
    write(DRAFTS_KEY, drafts)
    set({ drafts })
  },
  clearDraft: (key) => {
    if (!get().drafts[key]) {
      return
    }
    const drafts = { ...get().drafts }
    delete drafts[key]
    write(DRAFTS_KEY, drafts)
    set({ drafts })
  },
}))
